import qs from 'qs';
import instance from 'api';
import {
  IAppApartmentRoom,
  IAppApartmentRoomSaveRequest,
  IAppRealEstateFilterOptions,
  IAppRealEstateObject,
  IAppRoomType,
  IAppTemplate,
  IAppTemplateHistory,
  IAppTemplateIndicatorList,
  IAppTemplateIndicatorSaveRequest,
  IAppTemplateOption,
  IAppTemplateRealEstate,
  IAppTemplateRealEstateChangeResult,
  IAppTemplateSaveRequest,
  IAppZone,
  IAppZoneSaveRequest,
  IDefectType,
  IPage,
  TemplateGroup,
} from './interfaces';

// ---------- Шаблоны ----------
export const getTemplateList = (params: {
  templateGroup?: TemplateGroup;
  search?: string;
  pageNo: number;
  pageSize: number;
}) => {
  return instance()
    .get<IPage<IAppTemplate>>('/app/template/list', { params })
    .then((response) => response.data);
};

export const exportTemplateList = (params: { templateGroup: TemplateGroup; search?: string }) =>
  instance()
    .get<Blob>('/app/template/excel', { params: { ...params, pageNo: 1, pageSize: 10 }, responseType: 'blob' })
    .then((response) => response.data);

export const loadTemplate = (id: number) => {
  return instance()
    .get<IAppTemplate>(`/app/template/load/${id}`)
    .then((response) => response.data);
};

export const getTemplateOptions = () => {
  return instance()
    .get<IAppTemplateOption[]>('/app/template/options')
    .then((response) => response.data);
};

export const createTemplate = (data: IAppTemplateSaveRequest) => {
  return instance()
    .post<number>('/app/template/save', data)
    .then((response) => response.data);
};

export const updateTemplate = (id: number, data: IAppTemplateSaveRequest) => {
  return instance()
    .post(`/app/template/${id}/update`, data)
    .then((response) => response.data);
};

export const deleteTemplate = (id: number) => {
  return instance()
    .post(`/app/template/${id}/delete`)
    .then((response) => response.data);
};

export const moveTemplate = (id: number, templateGroup: TemplateGroup) => {
  return instance()
    .post(`/app/template/${id}/move`, { templateGroup })
    .then((response) => response.data);
};

// ---------- История ----------
export const getTemplateHistory = (params: { templateId: number; pageNo: number; pageSize: number }) => {
  return instance()
    .get<IPage<IAppTemplateHistory>>('/app/template/history/list', { params })
    .then((response) => response.data);
};

export const restoreDeletedTemplateIndicator = (historyId: number) => {
  return instance()
    .post('/app/template/indicator/restoreDeleted', { historyId })
    .then((response) => response.data);
};

// ---------- Конструктивы шаблона ----------
export const getTemplateIndicators = (templateId: number) => {
  return instance()
    .get<IAppTemplateIndicatorList>('/app/template/indicator/list', { params: { templateId } })
    .then((response) => response.data);
};

export const createTemplateIndicator = (data: IAppTemplateIndicatorSaveRequest) => {
  return instance()
    .post('/app/template/indicator/save', data)
    .then((response) => response.data);
};

export const updateTemplateIndicator = (indicatorId: number, data: IAppTemplateIndicatorSaveRequest) => {
  return instance()
    .post(`/app/template/indicator/${indicatorId}/update`, data)
    .then((response) => response.data);
};

export const excludeTemplateIndicator = (templateId: number, indicatorId: number) => {
  return instance()
    .post('/app/template/indicator/exclude', { templateId, indicatorId })
    .then((response) => response.data);
};

export const restoreTemplateIndicator = (templateId: number, indicatorId: number) => {
  return instance()
    .post('/app/template/indicator/restore', { templateId, indicatorId })
    .then((response) => response.data);
};

// ---------- Объекты недвижимости шаблона ----------
export const getTemplateRealEstates = (params: {
  templateId: number;
  search?: string;
  managementNames?: string[];
  sectionNames?: string[];
  pageNo: number;
  pageSize: number;
}) => {
  return instance()
    .get<IPage<IAppTemplateRealEstate>>('/app/template/realEstate/list', {
      params,
      // repeat: managementNames=A&managementNames=B — так Spring биндит поля-списки запроса
      paramsSerializer: (value) => qs.stringify(value, { arrayFormat: 'repeat', skipNulls: true }),
    })
    .then((response) => response.data);
};

export const changeTemplateRealEstate = (realEstateGuid: string, templateId: number) => {
  return instance()
    .post<IAppTemplateRealEstateChangeResult>('/app/template/realEstate/change', { realEstateGuid, templateId })
    .then((response) => response.data);
};

// ---------- Справочник комнат ----------
export const getApartmentRooms = () => {
  return instance()
    .get<IAppApartmentRoom[]>('/app/apartmentRoom/listAll')
    .then((response) => response.data);
};

export const createApartmentRoom = (data: IAppApartmentRoomSaveRequest) => {
  return instance()
    .post<number>('/app/apartmentRoom/save', data)
    .then((response) => response.data);
};

export const updateApartmentRoom = (id: number, data: IAppApartmentRoomSaveRequest) => {
  return instance()
    .post(`/app/apartmentRoom/${id}/update`, data)
    .then((response) => response.data);
};

export const reorderApartmentRooms = (orderedIds: number[]) => {
  return instance()
    .post('/app/apartmentRoom/reorder', { orderedIds })
    .then((response) => response.data);
};

export const deleteApartmentRoom = (id: number) => {
  return instance()
    .post(`/app/apartmentRoom/${id}/delete`)
    .then((response) => response.data);
};

// ---------- Справочник зон конструктива ----------
export const getAppZones = () => {
  return instance()
    .get<IAppZone[]>('/app/zone/listAll')
    .then((response) => response.data);
};

export const createAppZone = (data: IAppZoneSaveRequest) => {
  return instance()
    .post<number>('/app/zone/save', data)
    .then((response) => response.data);
};

export const updateAppZone = (id: number, data: IAppZoneSaveRequest) => {
  return instance()
    .post(`/app/zone/${id}/update`, data)
    .then((response) => response.data);
};

export const reorderAppZones = (orderedIds: number[]) => {
  return instance()
    .post('/app/zone/reorder', { orderedIds })
    .then((response) => response.data);
};

export const archiveAppZone = (id: number) => {
  return instance()
    .post(`/app/zone/${id}/archive`)
    .then((response) => response.data);
};

// ---------- Словари (переиспользуются) ----------
export const getRoomTypeListAll = () => {
  return instance()
    .get<IAppRoomType[]>('/app/roomType/listAllNotDeleted')
    .then((response) => response.data);
};

export const getDefectTypeList = () => {
  return instance()
    .post<IDefectType[]>('/dictionary/defects', {})
    .then((response) => response.data);
};

// ---------- Объекты недвижимости (страничный таб) ----------
export const getRealEstateObjects = (params: {
  search?: string;
  managementNames?: string[];
  sectionNames?: string[];
  templateIds?: number[];
  onlyUnassigned?: boolean;
  keyDateFrom?: string;
  keyDateTo?: string;
  pageNo: number;
  pageSize: number;
}) => {
  return instance()
    .get<IPage<IAppRealEstateObject>>('/app/template/realEstate/objects', {
      params,
      // repeat: managementNames=A&managementNames=B — так Spring биндит поля-списки запроса
      paramsSerializer: (value) => qs.stringify(value, { arrayFormat: 'repeat', skipNulls: true }),
    })
    .then((response) => response.data);
};

export const getRealEstateAwaitingCount = () => {
  return instance()
    .get<number>('/app/template/realEstate/objects/awaitingCount')
    .then((response) => response.data);
};

export const getRealEstateFilterOptions = () => {
  return instance()
    .get<IAppRealEstateFilterOptions>('/app/template/realEstate/filters')
    .then((response) => response.data);
};
